import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  UserCircle, Clock, ListChecks, Calendar, ShieldCheck, DeviceMobile, ArrowLeft,
} from "@phosphor-icons/react";

function fmtMs(ms) {
  if (!ms) return "0h 0m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}

export default function WorkforceStaffProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/workforce/staff/${id}/profile`);
      setP(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const resetDevice = async () => {
    if (!window.confirm("Unbind current device? Staff will need to re-login on a new phone.")) return;
    try {
      await api.post(`/workforce/staff/${id}/device/reset`);
      toast.success("Device unbound");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!p) return <AppShell title="Staff Profile"><div className="text-sm text-[#8A8780]">Loading…</div></AppShell>;
  const s = p.staff;

  return (
    <AppShell title={s.name} subtitle={`Staff 360° Profile · ${s.staff_code || ""}`}
      actions={
        <Link to="/staff" className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm">
          <ArrowLeft size={14}/>Back to list
        </Link>
      }>
      <div className="grid grid-cols-12 gap-5" data-testid="staff-profile">
        {/* Identity */}
        <div className="col-span-12 md:col-span-4 bg-white border border-[#E5E2D9] rounded-md p-5">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-full bg-[#D4E0D1] flex items-center justify-center">
              <UserCircle size={32} color="#2D5A27" weight="duotone"/>
            </div>
            <div>
              <div className="font-heading text-xl leading-tight">{s.name}</div>
              <div className="text-xs text-[#5C5A55] capitalize mt-1">{s.role}</div>
              <div className="text-[11px] font-mono text-[#8A8780] mt-0.5">{s.staff_code || "—"}</div>
            </div>
          </div>
          <div className="mt-5 space-y-2 text-sm">
            <Row k="Phone" v={s.phone}/>
            <Row k="Email" v={s.email || "—"}/>
            <Row k="Status" v={s.status}/>
            <Row k="Zone" v={s.zone_id || "—"}/>
            <Row k="Hourly Rate" v={`₹${s.hourly_rate || 0}/hr`}/>
            <Row k="Base Salary" v={`₹${s.base_salary || 0}/mo`}/>
            <Row k="Daily Hours" v={s.daily_hours ? `${s.daily_hours} h/day` : "tenant default"}/>
            <Row k="Joined" v={s.date_of_joining || "—"}/>
            <Row k="Emergency" v={s.emergency_contact || "—"}/>
          </div>
          <div className="mt-5 pt-4 border-t border-[#E5E2D9]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] flex items-center gap-1 mb-2"><DeviceMobile size={11}/>Device</div>
            <div className="font-mono text-xs break-all">{s.device_id || "— not bound —"}</div>
            {s.device_id && (
              <button onClick={resetDevice} data-testid="btn-reset-device"
                      className="mt-3 w-full h-9 text-xs border border-[#C25D38] text-[#C25D38] hover:bg-[#C25D38] hover:text-white rounded-md">
                Unbind device
              </button>
            )}
          </div>
        </div>

        {/* Main grid */}
        <div className="col-span-12 md:col-span-8 space-y-5">
          {/* Current session */}
          {p.current_session ? (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="current-session">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] flex items-center gap-1 mb-1"><Clock size={11}/>Live Duty Session</div>
              <div className="font-heading text-xl">{p.current_session.status}</div>
              <div className="grid grid-cols-4 gap-4 mt-4">
                <Kpi label="Active" v={fmtMs(p.current_session.active_ms)}/>
                <Kpi label="Break" v={fmtMs(p.current_session.break_ms)}/>
                <Kpi label="Inactive" v={fmtMs(p.current_session.inactive_ms)}/>
                <Kpi label="Total" v={fmtMs(p.current_session.total_ms)}/>
              </div>
            </div>
          ) : (
            <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-5 text-sm text-[#8A8780]">
              Not currently on duty.
            </div>
          )}

          {/* Task stats */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-1 mb-3"><ListChecks size={11}/>Tasks</div>
            <div className="grid grid-cols-3 gap-4">
              <Kpi label="Completed" v={p.tasks.completed}/>
              <Kpi label="Pending" v={p.tasks.pending}/>
              <Kpi label="Missed" v={p.tasks.missed}/>
            </div>
          </div>

          {/* Shifts */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-1 mb-3"><Calendar size={11}/>Shifts</div>
            {p.shifts.length === 0 && <div className="text-xs text-[#8A8780]">No shifts configured.</div>}
            <div className="flex flex-wrap gap-2">
              {p.shifts.map(sh => (
                <div key={sh.id} className="text-xs font-mono bg-[#D4E0D1] text-[#1A3317] px-2 py-1 rounded">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][sh.day_of_week]}: {sh.start_time}–{sh.end_time}
                </div>
              ))}
            </div>
          </div>

          {/* Sessions history */}
          <div className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
              Last 5 duty sessions
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#8A8780] bg-[#FAFAF7]">
                  <th className="px-5 py-2">Start</th>
                  <th className="px-5 py-2">End</th>
                  <th className="px-5 py-2 text-right">Active</th>
                  <th className="px-5 py-2 text-right">Break</th>
                  <th className="px-5 py-2 text-right">Idle</th>
                </tr>
              </thead>
              <tbody>
                {p.sessions.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-[#8A8780]">No sessions yet.</td></tr>}
                {p.sessions.map(s => (
                  <tr key={s.id} className="border-t border-[#E5E2D9]">
                    <td className="px-5 py-2 font-mono text-xs">{s.start_time ? new Date(s.start_time).toLocaleString() : "—"}</td>
                    <td className="px-5 py-2 font-mono text-xs">{s.end_time ? new Date(s.end_time).toLocaleString() : "—"}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMs(s.active_ms)}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMs(s.break_ms)}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMs(s.inactive_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Audit */}
          <div className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-1">
              <ShieldCheck size={11}/>Recent activity
            </div>
            <div className="max-h-80 overflow-y-auto">
              {p.audit.length === 0 && <div className="px-5 py-6 text-xs text-[#8A8780]">No audit records.</div>}
              {p.audit.map(a => (
                <div key={a.id} className="px-5 py-2 border-t border-[#E5E2D9] flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono text-[#2D5A27]">{a.action}</div>
                    {a.target && <div className="text-[10px] text-[#8A8780] font-mono break-all">{a.target}</div>}
                  </div>
                  <div className="text-[10px] text-[#8A8780]">{new Date(a.timestamp).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between">
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</span>
    <span className="font-mono text-xs text-[#2D2B26]">{v}</span>
  </div>
);

const Kpi = ({ label, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className="font-heading text-2xl leading-tight">{v}</div>
  </div>
);
